<template>
  <div class="recycle-time-slots-container" v-loading="syncing" element-loading-text="正在同步，请稍候...">
    <h1 class="page-title">到店回收时段设置</h1>
    
    <el-card v-loading="loading" element-loading-text="加载中..." class="content-card">
      <el-tabs v-model="activeTab" type="card">
        <el-tab-pane 
          v-for="day in days" 
          :key="day.value" 
          :label="day.label"
          :name="day.value.toString()"
        >
          <div class="tab-header">
            <el-button type="primary" @click="showAddDialog(parseInt(day.value))" :disabled="syncing">
              <el-icon><Plus /></el-icon>添加时段
            </el-button>
            <el-button type="success" @click="syncAll(parseInt(day.value))" style="margin-left: 10px" :loading="syncing">
              <el-icon><Refresh /></el-icon>同步所有
            </el-button>
          </div>
          
          <div class="tab-content">
            <el-table 
              :data="getSortedTimeSlots(parseInt(day.value))" 
              border 
              style="width: 100%"
              v-if="getSortedTimeSlots(parseInt(day.value)).length > 0"
            >
              <el-table-column prop="start_time" label="开始时间" width="120" />
              <el-table-column prop="end_time" label="结束时间" width="120" />
              <el-table-column prop="is_available" label="是否可用" width="120">
                <template #default="scope">
                  <el-switch v-model="scope.row.is_available" @change="handleToggleAvailability(scope.row)" />
                </template>
              </el-table-column>
              <el-table-column label="操作" width="180" fixed="right">
                <template #default="scope">
                  <el-button type="primary" size="small" @click="showEditDialog(scope.row)">
                    <el-icon><Edit /></el-icon>编辑
                  </el-button>
                  <el-button type="danger" size="small" @click="handleDelete(scope.row)">
                    <el-icon><Delete /></el-icon>删除
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <div v-else class="empty-state">
              <el-empty description="暂无时段设置" />
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>
    
    <!-- 添加/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="`${dialogTitle} -- ${getDayLabel(formData.day_of_week)}`"
      width="500px"
      @close="resetForm"
    >
      <el-form :model="formData" :rules="rules" ref="formRef" label-width="120px">
        
        <el-form-item label="开始时间" prop="start_time">
          <el-time-picker v-model="formData.start_time" placeholder="请选择开始时间" format="HH:mm" value-format="HH:mm" />
        </el-form-item>
        
        <el-form-item label="结束时间" prop="end_time">
          <el-time-picker v-model="formData.end_time" placeholder="请选择结束时间" format="HH:mm" value-format="HH:mm" />
        </el-form-item>
        
        <el-form-item label="是否可用" prop="is_available">
          <el-switch v-model="formData.is_available" />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveTimeSlot" :loading="saving">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { Plus, Edit, Delete, Refresh } from '@element-plus/icons-vue';
import { ElMessage, ElTable, ElTableColumn, ElButton, ElSelect, ElOption, ElSwitch, ElDialog, ElForm, ElFormItem, ElTimePicker, ElInputNumber, ElEmpty } from 'element-plus';
import request from '../../api/axios';

const timeSlots = ref([]);
const dialogVisible = ref(false);
const dialogTitle = ref('添加时段');
const saving = ref(false);
const loading = ref(true);
const syncing = ref(false); // 同步操作的loading状态
const formRef = ref(null);
const activeTab = ref('1'); // 默认选中周一

// 星期数组
const days = [
  { label: '周日', value: 0 },
  { label: '周一', value: 1 },
  { label: '周二', value: 2 },
  { label: '周三', value: 3 },
  { label: '周四', value: 4 },
  { label: '周五', value: 5 },
  { label: '周六', value: 6 }
];

const formData = reactive({
  id: '',
  day_of_week: '',
  start_time: null,
  end_time: null,
  is_available: true,
  max_appointments: 10,
  sort_order: 0
});

const rules = {
  day_of_week: [{ required: true, message: '请选择星期几', trigger: 'change' }],
  start_time: [
    { required: true, message: '请选择开始时间', trigger: 'change' },
    { pattern: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, message: '开始时间必须符合HH:mm格式', trigger: 'change' }
  ],
  end_time: [
    { required: true, message: '请选择结束时间', trigger: 'change' },
    { pattern: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, message: '结束时间必须符合HH:mm格式', trigger: 'change' }
  ],
  max_appointments: [{ required: true, message: '请输入最大预约数', trigger: 'blur', type: 'number' }],
  sort_order: [{ required: true, message: '请输入排序', trigger: 'blur', type: 'number' }]
};

// 获取所有时段设置
const getTimeSlots = async () => {
  loading.value = true;
  try {
    const response = await request({
      url: '/manage/recycle-time-slots',
      method: 'get'
    });
    
    if (response.code === 200) {
      timeSlots.value = response.data;
    }
  } catch (error) {
    ElMessage.error('获取时段设置失败');
    console.error('获取时段设置失败:', error);
  } finally {
    loading.value = false;
  }
};

// 获取指定日期的时段列表
const getDayTimeSlots = (dayOfWeek) => {
  return timeSlots.value.filter(item => item.day_of_week === dayOfWeek);
};

// 获取指定日期的时段列表并按开始时间升序排序
const getSortedTimeSlots = (dayOfWeek) => {
  const slots = getDayTimeSlots(dayOfWeek);
  // 按照开始时间升序排序
  return slots.sort((a, b) => {
    if (a.start_time < b.start_time) return -1;
    if (a.start_time > b.start_time) return 1;
    return 0;
  });
};

// 显示添加对话框
const showAddDialog = (dayOfWeek) => {
  dialogTitle.value = '添加时段';
  resetForm();
  formData.day_of_week = dayOfWeek;
  dialogVisible.value = true;
};

// 显示编辑对话框
const showEditDialog = (row) => {
  dialogTitle.value = '编辑时段';
  Object.assign(formData, row);
  dialogVisible.value = true;
};

// 保存时段设置
const saveTimeSlot = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        saving.value = true;
        let response;
        
        if (formData.id) {
          // 编辑
          response = await request({
            url: `/manage/recycle-time-slots/${formData.id}`,
            method: 'put',
            data: formData
          });
        } else {
          // 添加
          response = await request({
            url: '/manage/recycle-time-slots',
            method: 'post',
            data: formData
          });
        }
        
        if (response.code === 200) {
          ElMessage.success(formData.id ? '更新成功' : '添加成功');
          dialogVisible.value = false;
          getTimeSlots();
        }
      } catch (error) {
        ElMessage.error(formData.id ? '更新失败' : '添加失败');
        console.error('保存时段设置失败:', error);
      } finally {
        saving.value = false;
      }
    }
  });
};

// 删除时段设置
const handleDelete = async (row) => {
  try {
    const response = await request({
      url: `/manage/recycle-time-slots/${row.id}`,
      method: 'delete'
    });
    
    if (response.code === 200) {
      ElMessage.success('删除成功');
      getTimeSlots();
    }
  } catch (error) {
    ElMessage.error('删除失败');
    console.error('删除时段设置失败:', error);
  }
};

// 切换可用性
const handleToggleAvailability = async (row) => {
  try {
    const response = await request({
      url: `/manage/recycle-time-slots/${row.id}`,
      method: 'put',
      data: {
        is_available: row.is_available
      }
    });
    
    if (response.code === 200) {
      ElMessage.success('更新成功');
    } else {
      // 更新失败，恢复原来的状态
      row.is_available = !row.is_available;
      ElMessage.error('更新失败');
    }
  } catch (error) {
    // 更新失败，恢复原来的状态
    row.is_available = !row.is_available;
    ElMessage.error('更新失败');
    console.error('更新时段可用性失败:', error);
  }
};

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
  Object.assign(formData, {
    id: '',
    day_of_week: '',
    start_time: null,
    end_time: null,
    is_available: true,
    max_appointments: 10,
    sort_order: 0
  });
};

// 获取星期几的中文标签
const getDayLabel = (day) => {
  if (day === '' || day === undefined || day === null) {
    return '';
  }
  const labels = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  return labels[day] || day;
};

// 同步所有功能：将当前日期的时段设置同步到所有其他日期
const syncAll = async (sourceDay) => {
  try {
    // 开始同步，显示loading
    syncing.value = true;
    
    // 获取当前日期的时段设置
    const sourceTimeSlots = getDayTimeSlots(sourceDay);
    
    if (sourceTimeSlots.length === 0) {
      ElMessage.warning('当前日期没有时段设置，无法同步');
      return;
    }
    
    // 显示确认消息
    ElMessage.success('正在同步，请稍候...');
    
    // 确保时间格式为 H:i（去掉可能的秒数）
    const formatTime = (time) => {
      if (!time) return time;
      // 如果时间包含秒数，去掉秒数部分
      if (time.includes(':')) {
        const parts = time.split(':');
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
      return time;
    };
    
    // 收集所有需要删除的ID
    const allIdsToDelete = [];
    // 收集所有需要创建的数据
    const allDataToCreate = [];
    
    // 遍历其他日期
    for (const day of days) {
      const targetDay = day.value;
      if (targetDay === sourceDay) continue; // 跳过源日期
      
      // 1. 获取目标日期的所有现有时段
      const targetTimeSlots = getDayTimeSlots(targetDay);
      
      // 2. 收集需要删除的ID
      for (const slot of targetTimeSlots) {
        allIdsToDelete.push(slot.id);
      }
      
      // 3. 收集需要创建的数据
      for (const slot of sourceTimeSlots) {
        allDataToCreate.push({
          day_of_week: targetDay,
          start_time: formatTime(slot.start_time),
          end_time: formatTime(slot.end_time),
          is_available: slot.is_available,
          max_appointments: slot.max_appointments,
          sort_order: slot.sort_order
        });
      }
    }
    
    // 执行批量操作
    // 1. 批量删除（如果有需要删除的ID）
    if (allIdsToDelete.length > 0) {
      await request({
        url: '/manage/recycle-time-slots/batch-destroy',
        method: 'post',
        data: { ids: allIdsToDelete }
      });
    }
    
    // 2. 批量创建（如果有需要创建的数据）
    if (allDataToCreate.length > 0) {
      await request({
        url: '/manage/recycle-time-slots/batch-store',
        method: 'post',
        data: { time_slots: allDataToCreate }
      });
    }
    
    // 重新获取所有时段
    await getTimeSlots();
    ElMessage.success('同步成功！所有日期已更新为当前日期的时段设置');
  } catch (error) {
    ElMessage.error('同步失败');
    console.error('同步时段设置失败:', error);
  } finally {
    // 同步结束，隐藏loading
    syncing.value = false;
  }
};

onMounted(() => {
  getTimeSlots();
});
</script>

<style scoped>
.recycle-time-slots-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

/* 增强标签页样式 */
:deep(.el-tabs__item) {
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 12px 24px;
}

/* 激活状态的标签页样式 - 与主题按钮颜色一致 */
:deep(.el-tabs__item.is-active) {
  color: var(--el-color-primary);
  font-weight: 700;
  background-color: var(--el-color-primary-light-9);
  border-color: var(--el-color-primary);
  box-shadow: 0 2px 8px var(--el-color-primary-light-5);
}

/* 标签页悬停效果 - 与主题按钮颜色一致 */
:deep(.el-tabs__item:hover) {
  color: var(--el-color-primary-light-3);
  background-color: var(--el-color-primary-light-9);
}

/* 标签页内容区样式 */
:deep(.el-tabs__content) {
  padding: 20px 0;
}

.tab-header {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
}

.tab-content {
  padding: 0;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>